import React, {useState} from 'react';
import {Popover, Text} from '@mantine/core';

import {Container, Input, OptionList, Option} from './styles';
import usePrepareOrder from 'modules/orders/hooks/use-prepare-order';
import usePreparedOrder from 'modules/orders/hooks/use-prepared-order';
import useReadyToDelivery from 'modules/orders/hooks/use-ready-to-delivery';
import useDeliveryOrder from 'modules/orders/hooks/use-delivery-order';
import useFinishOrder from 'modules/orders/hooks/use-finish-order';
import useCancelOrder from 'modules/orders/hooks/use-cancel-order';
import {OrderProps, OrderStatus, OrderTypeEnum} from 'modules/orders/types';
import IconButton from 'shared/migration-components/icon-button';
import {openConfirmModal} from '@mantine/modals';
import {UseMutationResult} from 'react-query';
import useAccessControls from 'modules/access-controls/atoms/use-access-controls';
import useReopenOrder from 'modules/orders/hooks/use-reopen-order';

const OrderStatusSelect: React.FC<OrderProps> = ({
  id,
  currentStatusName,
  type,
}) => {
  const [opened, setOpened] = useState(false);
  const [searchText, setSearch] = useState('');
  const {accessControl} = useAccessControls();

  const orderStatusSelect = [
    {
      label: 'Novo pedido',
      value: OrderStatus.opened,
      hidden: !!accessControl?.companyId,
    },
    {label: 'Em preparo', value: OrderStatus.preparing},
    {
      label: 'Pronto para retirada',
      value: OrderStatus.ready_to_pickup,
    },
    {
      label: 'Pronta para entrega',
      value: OrderStatus.ready_to_delivery,
    },
    {
      label: 'Em entrega',
      value: OrderStatus.delivering,
      hidden: !!accessControl?.companyId,
    },
    {
      label: 'Avisar cliente',
      value: OrderStatus.arrival,
      hidden: !!accessControl?.companyId,
    },
    {
      label: 'Finalizado',
      value: OrderStatus.finished,
      hidden: !!accessControl?.companyId && type !== OrderTypeEnum.pickup,
    },
    {label: 'Cancelado', value: OrderStatus.canceled},
  ];

  const availableOptions = orderStatusSelect
    .filter((option) => !option.hidden)
    .filter(
      (option) =>
        option.value !== OrderStatus.ready_to_pickup ||
        type === OrderTypeEnum.pickup,
    )
    .filter(
      (option) =>
        option.value !== OrderStatus.ready_to_delivery ||
        type === OrderTypeEnum.delivery,
    )
    .filter((option) => option.value !== currentStatusName)
    .filter(
      (option) =>
        option.label
          .toLocaleLowerCase()
          .search(searchText.toLocaleLowerCase()) >= 0,
    );

  const reopenOrderMutation = useReopenOrder(id);
  const preparingMutation = usePrepareOrder(id);
  const readToPickupMutation = usePreparedOrder(id);
  const readyToDeliveryMutation = useReadyToDelivery(id);
  const deliveringMutation = useDeliveryOrder(id);
  const finishedMutation = useFinishOrder(id);
  const canceledMutation = useCancelOrder(id);

  const mutationsByStatus: Partial<
    Record<OrderStatus, UseMutationResult<any, unknown, any, unknown>>
  > = {
    opened: reopenOrderMutation,
    preparing: preparingMutation,
    ready_to_pickup: readToPickupMutation,
    ready_to_delivery: readyToDeliveryMutation,
    delivering: deliveringMutation,
    finished: finishedMutation,
    canceled: canceledMutation,
  };

  const isLoading =
    preparingMutation.isLoading ||
    readToPickupMutation.isLoading ||
    readyToDeliveryMutation.isLoading ||
    deliveringMutation.isLoading ||
    finishedMutation.isLoading ||
    canceledMutation.isLoading ||
    reopenOrderMutation.isLoading;

  const openModal = (status: OrderStatus) =>
    openConfirmModal({
      title: 'Alterar status',
      children: <Text>Deseja realmente alterar o status do pedido?</Text>,
      labels: {confirm: 'Sim', cancel: 'Não'},
      onConfirm: () => mutationsByStatus[status]?.mutate(undefined),
    });

  return (
    <Popover
      key={id}
      withinPortal
      trapFocus={false}
      opened={opened}
      onClose={() => setOpened(false)}
      zIndex={1001}
      keepMounted
      styles={{dropdown: {padding: 0}}}>
      <Popover.Target>
        <div
          onClick={() => {
            setOpened(true);
          }}>
          <IconButton
            variant="filled"
            label="Alterar status"
            loading={isLoading}
          />
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Container>
          <Input
            placeholder="Pesquisar"
            autoFocus
            onChange={(e) => setSearch(e.target.value)}
          />
          <OptionList>
            {availableOptions.map((option, index) => (
              <Option
                className="ignore"
                key={index}
                onClick={() => {
                  openModal(option.value);
                  setOpened(false);
                }}>
                {option?.label}
              </Option>
            ))}
          </OptionList>
        </Container>
      </Popover.Dropdown>
    </Popover>
  );
};

export default OrderStatusSelect;
